import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

/**
 * Вкладки. По умолчанию — сегментированный переключатель из макетов (подложка muted,
 * активная вкладка — surface + тень). `variant="underline"` у TabsList — вкладки с
 * подчёркиванием accent (языки сообщения в «Создать уведомление»).
 */
type TabsVariant = "segmented" | "underline"
const TabsVariantContext = React.createContext<TabsVariant>("segmented")

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { variant?: TabsVariant }
>(({ className, variant = "segmented", ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        variant === "segmented"
          ? "inline-flex items-center gap-1 rounded-xl bg-muted p-1 text-text-2"
          : "flex items-end gap-1.5 border-b border-border-soft text-text-2",
        className
      )}
      {...props} />
  </TabsVariantContext.Provider>
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext)
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-sm font-semibold outline-none transition-[background-color,color,box-shadow] hover:text-text focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:font-bold data-[state=active]:text-text",
        variant === "segmented"
          ? "h-8 rounded-[9px] px-3.5 data-[state=active]:bg-surface data-[state=active]:shadow-card"
          : "h-[38px] px-3 data-[state=active]:shadow-[inset_0_-2px_0_var(--accent)]",
        className
      )}
      {...props} />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-3 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      className
    )}
    {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
